<template>
    <app-layout>
        <div class="intelligence">
            <aside class="intelligence__sidebar">
                <div class="intelligence__brand">
                    <h2 class="intelligence__brand-title">{{ $t('pages.intelligence.menu_label') }}</h2>
                </div>
                <nav class="intelligence__nav">
                    <router-link
                        v-for="item in navItems"
                        :key="item.name"
                        :to="{ name: item.name }"
                        class="intelligence__nav-item"
                        activeClass="intelligence__nav-item--active"
                    >
                        {{ item.label }}
                    </router-link>
                </nav>
            </aside>

            <main class="intelligence__main">
                <header class="intelligence__header">
                    <div class="intelligence__header-text">
                        <h1 class="intelligence__title">{{ title }}</h1>
                        <p v-if="subtitle" class="intelligence__subtitle">{{ subtitle }}</p>
                    </div>
                    <div class="intelligence__filters">
                        <form-wrapper>
                            <form-row class="intelligence__filter-row" align="bottom">
                                <form-cell class="intelligence__filter-cell intelligence__filter-cell--date">
                                    <date-range-field
                                        :label="$t('pages.intelligence.filters.time_range')"
                                        :formData="data"
                                        openSide="right"
                                        :appendToBody="true"
                                        valuePath="dateRange"
                                        @change="onFilterChange($event)"
                                    ></date-range-field>
                                </form-cell>
                                <form-cell class="intelligence__filter-cell">
                                    <select-field
                                        :label="$t('pages.intelligence.filters.specialty')"
                                        :placeholder="$t('pages.intelligence.filters.any')"
                                        :options="specialityOptions"
                                        :formData="data"
                                        valuePath="specialityId"
                                        @change="onFilterChange($event)"
                                    ></select-field>
                                </form-cell>
                                <form-cell class="intelligence__filter-cell">
                                    <select-field
                                        :label="$t('pages.intelligence.filters.payer')"
                                        :placeholder="$t('pages.intelligence.filters.any')"
                                        :groupOptions="true"
                                        :isSearchable="true"
                                        :options="insurancePlanOptions"
                                        :formData="data"
                                        valuePath="insurancePlanId"
                                        @change="onFilterChange($event)"
                                    ></select-field>
                                </form-cell>
                                <form-cell class="intelligence__filter-cell">
                                    <select-field
                                        :label="$t('pages.intelligence.filters.state')"
                                        :placeholder="$t('pages.intelligence.filters.any')"
                                        :isSearchable="true"
                                        :options="stateOptions"
                                        :formData="data"
                                        valuePath="state"
                                        @change="onFilterChange($event)"
                                    ></select-field>
                                </form-cell>
                                <form-cell class="intelligence__filter-cell">
                                    <text-field
                                        :label="$t('pages.intelligence.filters.zip')"
                                        :placeholder="$t('pages.intelligence.filters.zip_placeholder')"
                                        :formData="data"
                                        valuePath="zip"
                                        @change="onFilterChange($event)"
                                    ></text-field>
                                </form-cell>
                            </form-row>
                        </form-wrapper>
                    </div>
                </header>

                <div class="intelligence__content">
                    <slot></slot>
                </div>
            </main>
        </div>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import FormMixin from 'mixins/FormMixin'
    import dayjs from 'dayjs'

    export default {
        mixins: [FormMixin],

        components: {
            AppLayout,
        },

        props: {
            title: {
                type: String,
                required: true,
            },
            subtitle: {
                type: String,
                default: '',
            },
            specialityOptions: {
                type: Array,
                default: () => [],
            },
            stateOptions: {
                type: Array,
                default: () => [],
            },
            insurancePlanOptions: {
                type: Array,
                default: () => [],
            },
        },

        computed: {
            navItems() {
                return [
                    {
                        name: 'intelligence.overview',
                        label: this.$t('pages.intelligence.nav.overview'),
                    },
                    {
                        name: 'intelligence.constraints',
                        label: this.$t('pages.intelligence.nav.constraints'),
                    },
                    {
                        name: 'intelligence.delivery',
                        label: this.$t('pages.intelligence.nav.delivery'),
                    },
                    {
                        name: 'intelligence.supply-demand',
                        label: this.$t('pages.intelligence.nav.supply_demand'),
                    },
                    {
                        name: 'intelligence.entitlement',
                        label: this.$t('pages.intelligence.nav.entitlement'),
                    },
                    {
                        name: 'intelligence.patient-friction',
                        label: this.$t('pages.intelligence.nav.patient_friction'),
                    },
                    {
                        name: 'intelligence.provider-performance',
                        label: this.$t('pages.intelligence.nav.provider_performance'),
                    },
                    {
                        name: 'intelligence.provider-competition',
                        label: this.$t('pages.intelligence.nav.provider_competition'),
                    },
                ]
            },
        },

        methods: {
            onFilterChange(event) {
                if (event.path === 'zip' && event.value.length !== 5 && event.value.length !== 0) {
                    return
                }
                this.change(event)
                this.emitFilters()
            },

            emitFilters() {
                const { startDate, endDate } = this.data.dateRange || {}
                if (!startDate || !endDate) return

                this.$emit('filters-change', {
                    fromDate: dayjs(startDate).format('YYYY-MM-DD'),
                    toDate: dayjs(endDate).format('YYYY-MM-DD'),
                    specialityId: this.data.specialityId || null,
                    insurancePlanId: this.data.insurancePlanId || null,
                    state: this.data.state || null,
                    zip: this.data.zip || null,
                })
            },
        },

        created() {
            const endDate = new Date()
            const startDate = new Date()
            startDate.setDate(startDate.getDate() - 30)

            this.data.dateRange = {
                startDate,
                endDate,
            }
            this.data.specialityId = null
            this.data.insurancePlanId = null
            this.data.state = null
            this.data.zip = null
        },

        mounted() {
            this.emitFilters()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/variables';

    .intelligence {
        display: flex;
        min-height: calc(100vh - 80px);
        background: #f9fafb;
    }

    .intelligence__sidebar {
        width: 240px;
        flex-shrink: 0;
        background: #fff;
        border-right: 1px solid #e5e7eb;
        padding: 24px 0;
    }

    .intelligence__brand {
        padding: 0 20px 20px;
        border-bottom: 1px solid #e5e7eb;
        margin-bottom: 12px;
    }

    .intelligence__brand-title {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: #1e3a8a;
    }

    .intelligence__nav {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 0 12px;
    }

    .intelligence__nav-item {
        display: block;
        padding: 10px 12px;
        border-radius: 8px;
        color: #374151;
        text-decoration: none;
        font-size: 14px;
        font-weight: 500;
        transition: background 0.15s ease;

        &:hover {
            background: #f3f4f6;
        }

        &--active {
            background: #1e3a8a;
            color: #fff;
        }
    }

    .intelligence__main {
        flex: 1;
        min-width: 0;
        padding: 24px 32px;
    }

    .intelligence__header {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
        margin-bottom: 24px;
    }

    .intelligence__title {
        margin: 0 0 4px;
        font-size: 28px;
        font-weight: 700;
        color: #111827;
    }

    .intelligence__subtitle {
        margin: 0;
        font-size: 14px;
        color: #6b7280;
    }

    .intelligence__filters {
        width: 100%;
        min-width: 0;
        position: relative;
        z-index: 3;
        overflow: visible;
    }

    .intelligence__filter-row {
        flex-wrap: wrap;
        row-gap: 8px;
    }

    .intelligence__filter-cell {
        flex: 1 1 240px;
        min-width: 220px;
        max-width: 100%;
        margin-bottom: 0;
    }

    .intelligence__filter-cell--date {
        flex: 1.4 1 380px;
        min-width: 340px;
    }

    .intelligence__filters ::v-deep .vue-daterange-picker {
        display: block;
        width: 100%;
    }

    .intelligence__filters ::v-deep .reportrange-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .intelligence__filters ::v-deep .form-input-select__icon-label {
        min-width: 0;
        overflow: hidden;
    }

    .intelligence__filters ::v-deep .form-input-select__label {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .intelligence__filters ::v-deep .form-input-select__options {
        max-height: 320px;
        overflow-x: hidden;
    }

    .intelligence__filters ::v-deep .form-input-select__option,
    .intelligence__filters ::v-deep .form-input-select__options-heading {
        height: auto;
        min-height: 40px;
        align-items: flex-start;
        white-space: normal;
        padding-top: 8px;
        padding-bottom: 8px;
    }

    .intelligence__filters ::v-deep .form-input-select__option span {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
        overflow-wrap: anywhere;
        line-height: 1.35;
        flex: 1;
    }

    .intelligence__content {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    @media (max-width: 900px) {
        .intelligence {
            flex-direction: column;
        }

        .intelligence__sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #e5e7eb;
        }

        .intelligence__nav {
            flex-direction: row;
            flex-wrap: wrap;
        }

        .intelligence__main {
            padding: 16px;
        }

        .intelligence__filter-cell,
        .intelligence__filter-cell--date {
            flex-basis: 100%;
            min-width: 0;
        }
    }
</style>
